import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { AppTypes } from '@src/reducers/app.reducer'
import React from 'react'
import MenuItemUI from '@src/components/Elements/MenuItem/MenuItem'

interface MenuItemProps {
  isCollapsed: boolean
  label: string
  onAction?: () => void
  children?: React.ReactNode
  linkPath?: string
  icon: React.ReactElement
  collapsedOnly?: boolean
  visible?: boolean
}

const MenuItem = ({
  isCollapsed,
  label,
  onAction = () => null,
  children,
  linkPath = '#',
  icon,
  collapsedOnly,
  visible = true,
}: MenuItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number | undefined>(0)
  const dispatch = useDispatch()
  const { pathname } = useLocation()

  const isActive = linkPath && pathname === linkPath
  const isChildActive = React.Children.toArray(children).some(
    (child: any) => child.props.linkPath && pathname === child.props.linkPath
  )

  useEffect(() => {
    if (isActive || isChildActive) {
      setIsExpanded(true)
    } else {
      setIsExpanded(false)
    }
  }, [isActive, isChildActive])

  const handleLinkClick = (e: React.MouseEvent) => {
    if (!e.metaKey && !e.ctrlKey) {
      e.preventDefault()
      onAction()
    }
  }

  const handleToggle = () => {
    if (!children) {
      dispatch({ type: AppTypes.CLOSE_MENU })
    } else if (children && !isCollapsed) {
      setIsExpanded((prev) => !prev)
    }
  }

  useEffect(() => {
    if (contentRef.current) {
      setHeight(isExpanded ? contentRef.current.scrollHeight : 0)
    }
  }, [isExpanded, children])

  const iconWithSize = React.cloneElement(icon, { width: '16', height: '16' })

  if ((collapsedOnly && !isCollapsed) || !visible) {
    return null
  }

  return (
    <div>
      <MenuItemUI
        isCollapsed={isCollapsed}
        isActive={!!isActive}
        label={label}
        icon={iconWithSize}
        children={children}
        linkPath={linkPath}
        isExpanded={isExpanded}
        onLinkClick={handleLinkClick}
        onToggle={handleToggle}
      />
      {!isCollapsed && children && (
        <div
          ref={contentRef}
          style={{ maxHeight: `${height}px` }}
          className="transition-all duration-300 ease-in-out overflow-hidden"
        >
          <div className="pb-10">{children}</div>
        </div>
      )}
    </div>
  )
}

export default MenuItem
